/**
 * export-report — „Eksport raportu" z pulpitu: składa plik CSV z danych, które kafelki mają już
 * wczytane w pamięci (cache zapytań), bez dodatkowych zapytań do serwera. Dzięki temu raport
 * zawiera dokładnie to, co użytkownik widzi (jego firma/zakres, okres i uprawnienia).
 *
 * Format: separator `;`, cudzysłowy w polach ze znakami specjalnymi i BOM UTF-8, żeby Excel w
 * polskiej wersji poprawnie otworzył polskie znaki i kolumny.
 */

import type {
  BreakdownResponse,
  ChartResponse,
  FlowResponse,
  KpiResponse,
  ProcessPerformanceResponse,
  TransactionRow,
} from './dashboard-api';

type Translate = (key: string, opts?: Record<string, unknown>) => string;

export interface ReportEntry {
  /** Klucz zapytania: `['tenant', 'dashboard', <rodzaj>, ...parametry]`. */
  queryKey: readonly unknown[];
  data: unknown;
}

const HEADER = ['Sekcja', 'Pozycja', 'Szczegół', 'Wartość', 'Wartość 2'];

function cell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  return /[;"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function isoDay(iso: string): string {
  return iso.slice(0, 10);
}

/** Zamienia wczytane dane kafelków na tekst CSV (z nagłówkiem, bez BOM). */
export function buildDashboardCsv(entries: ReportEntry[], t: Translate): string {
  const rows: (string | number | null)[][] = [];

  for (const { queryKey, data } of entries) {
    const kind = queryKey[2];
    if (!data || typeof data !== 'object') continue;

    switch (kind) {
      case 'kpi': {
        const d = data as KpiResponse;
        rows.push([
          t('tenant.dashboard.widgets.kpi.label'),
          t(`tenant.dashboard.metrics.${d.metric}`),
          t(`tenant.dashboard.period.${d.period}`),
          d.count,
          d.previous ?? null,
        ]);
        break;
      }
      case 'chart': {
        const d = data as ChartResponse;
        const label = t(`tenant.dashboard.metrics.${d.entity}`);
        for (const p of d.series) {
          rows.push([
            t('tenant.dashboard.widgets.chart.label'),
            label,
            isoDay(p.bucket),
            p.count,
            null,
          ]);
        }
        break;
      }
      case 'breakdown': {
        const d = data as BreakdownResponse;
        for (const item of d.items) {
          rows.push([
            t('tenant.dashboard.widgets.breakdown.label'),
            t(`tenant.dashboard.metrics.${d.metric}`),
            item.name,
            item.count,
            null,
          ]);
        }
        break;
      }
      case 'flow': {
        const d = data as FlowResponse;
        for (const p of d.series) {
          rows.push([
            t('tenant.dashboard.widgets.flow.label'),
            t('tenant.dashboard.widgets.flow.created'),
            isoDay(p.bucket),
            p.created,
            p.completed,
          ]);
        }
        break;
      }
      case 'process-performance': {
        const d = data as ProcessPerformanceResponse;
        for (const p of d.processes) {
          rows.push([
            t('tenant.dashboard.widgets.process_performance.label'),
            p.name,
            `${p.total}`,
            p.onTimePct,
            null,
          ]);
        }
        break;
      }
      case 'transactions': {
        for (const r of data as TransactionRow[]) {
          rows.push([
            t('tenant.dashboard.widgets.transactions.label'),
            r.number ?? r.title,
            r.counterparty,
            r.amountGross,
            t(`tenant.dashboard.widgets.transactions.status.${r.status}`),
          ]);
        }
        break;
      }
      default:
        break;
    }
  }

  return [HEADER, ...rows].map((r) => r.map(cell).join(';')).join('\r\n');
}

/** Zapisuje tekst jako plik CSV (z BOM UTF-8) przez tymczasowy link pobierania. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
